import { styled } from 'opensolar-ui'
import { useErrorHandling, useTranslate } from 'ra-core'
import { FC, useEffect, useMemo, useState } from 'react'
import { http } from '../../services/http'
import { BlogConfig } from '../expoStand'
import { formatDate, isValidImageUrl } from '../utils'
import ContentSlider from './common/ContentSlider'
import { CardWrapper, SectionWrapper } from './common/styles'
import DefaultThumbnail from './default_thumbnail.jpg'

interface PropTypes {
  blogs: BlogConfig[]
  onBlogClick?: (url: string) => void
}

const Blogs: FC<PropTypes> = ({ blogs, onBlogClick }) => {
  const translate = useTranslate()
  const errorHandling = useErrorHandling()
  const [blogsLoaded, setBlogsLoaded] = useState<BlogConfig[]>([])
  useEffect(() => {
    //add in blogs that already have loaded metadata
    const blogsLoaded = blogs.filter((x) => x.url && x.title && x.image)
    const blogsToLoad = blogs.filter((x) => x.url && x.title === undefined).map((x) => x.url)
    if (blogsToLoad.length) {
      http.os
        .post(blogsToLoad, { url: 'partners/url_metadata/' })
        .then((res) => {
          if (res.data) setBlogsLoaded([...blogsLoaded, ...res.data])
        })
        .catch((err) => {
          setBlogsLoaded(blogsLoaded)
          errorHandling.data_provider(err, { msg: 'Failed to retrieve blog data' })
        })
    } else {
      setBlogsLoaded(blogsLoaded)
    }
  }, [blogs])
  if (!blogsLoaded.length) return null
  return (
    <SectionWrapper>
      <ContentSlider
        cards={blogsLoaded}
        header={translate('Blogs')}
        render={(blog, i) => <BlogCard {...blog} key={'blog-' + i} onBlogClick={onBlogClick} />}
      />
    </SectionWrapper>
  )
}

const Content = styled('div', { name: 'Content' })({
  lineHeight: '20px',
  maxWidth: 256,
  minHeight: 75,
  '& p': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    webkitLineClamp: 3,
    webkitBoxOrient: 'vertical',
  },
})

const Thumbnail = styled('img', { name: 'Thumbnail' })({
  height: 150,
  width: 265,
  objectFit: 'contain',
})

const Subtext = styled('span', { name: 'Subtext' })({
  color: '#4F4D55',
  fontSize: 12,
})

const BlogCard: FC<BlogConfig & { onBlogClick?: (url: string) => void }> = ({
  url,
  image,
  title,
  author,
  publish_date,
  onBlogClick,
}) => {
  const [imgSrc, setImgSrc] = useState(DefaultThumbnail)
  useEffect(() => {
    let isMounted = true
    if (image) {
      isValidImageUrl(image).then((works) => {
        if (isMounted) setImgSrc(works ? image : DefaultThumbnail)
      })
    } else {
      setImgSrc(DefaultThumbnail)
    }
    return () => {
      isMounted = false
    }
  }, [image])

  const formattedDate = useMemo(() => formatDate(publish_date), [publish_date])
  return (
    <CardWrapper
      onClick={() => {
        window.open(url, '_blank')?.focus()
        onBlogClick?.(url)
      }}
    >
      <Thumbnail src={imgSrc} />
      <Content>
        <p>
          <strong>{title || url}</strong>
        </p>
        <Subtext>
          {author} {formattedDate ? `● ${formattedDate}` : ''}
        </Subtext>
      </Content>
    </CardWrapper>
  )
}

export default Blogs
